import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import FormInputWithLabel from '../form/FormInputWithLabel';
import { fetchPatientDetailsUuid } from '../../../Redux/Thunk/Actions';

/**
 * CouplePatientInfo
 * Standardized header component for infertility and IVF clinical templates.
 * Displays Couple ID banner + dual partner info (Male and Female) side-by-side.
 * For Oocyte Freezing patients (or female patients without Couple ID), falls back
 * to displaying only the female patient's individual UHID and details.
 */
export default function CouplePatientInfo({
  patientsData = {},
  show = false,
  formData = {},
  setFormData,
  handleChange,
  loaderchange,
  onCoupleLoaded
}) {
  const dispatch = useDispatch();
  const [isCouple, setIsCouple] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const isOocyteFreezing =
        (patientsData?.purpose || '').toLowerCase().includes('oocyte') ||
        (patientsData?.purpose || '').toLowerCase().includes('freezing');

      if (!patientsData?.p_uhid) return;

      // If explicitly oocyte freezing without partner, treat as individual female UHID
      if (isOocyteFreezing && !patientsData?.couple_id) {
        setIsCouple(false);
        const name = `${patientsData.titles || ''} ${patientsData.first_name || ''} ${patientsData.last_name || ''}`.trim();
        setFormData(prev => ({
          ...prev,
          coupleId: '',
          isSingleFemale: true,
          patientId2: patientsData.p_uhid,
          patientName2: name,
          gender2: patientsData.gender || 'Female',
          age2: patientsData.age || '',
          patientId: patientsData.p_uhid,
          patientName: name
        }));
        return;
      }

      loaderchange?.('true');
      try {
        const payload = { p_uhid: patientsData.p_uhid };
        const action = fetchPatientDetailsUuid(payload);
        const response = await dispatch(action);

        if (response.payload?.status?.code === 200) {
          const list = response.payload.data?.data || [];
          const coupleId =
            list[0]?.couple_id ||
            list[1]?.couple_id ||
            patientsData.couple_id ||
            '';

          // Condition for Couple: has 2 linked partners or has a couple_id, AND is not purely oocyte freezing
          if (list.length >= 2 || (coupleId && !isOocyteFreezing)) {
            setIsCouple(true);
            const data1 =
              list[0]?.gender === 'Male' ? list[0] : list[1] || list[0];
            const data2 =
              list[0]?.gender === 'Female' ? list[0] : list[1] || list[1];

            const namePat1 = data1
              ? `${data1.titles || ''} ${data1.first_name || ''} ${data1.last_name || ''}`.trim()
              : '';
            const namePat2 = data2
              ? `${data2.titles || ''} ${data2.first_name || ''} ${data2.last_name || ''}`.trim()
              : '';

            setFormData(prev => ({
              ...prev,
              coupleId: coupleId,
              isSingleFemale: false,
              patientId1: data1?.p_uhid || '',
              patientName1: namePat1,
              gender1: data1?.gender || 'Male',
              age1: data1?.age || '',
              patientId2: data2?.p_uhid || '',
              patientName2: namePat2,
              gender2: data2?.gender || 'Female',
              age2: data2?.age || '',
              patientOne_UUId: data1?.id || '',
              patientTwo_UUId: data2?.id || ''
            }));

            if (onCoupleLoaded) {
              onCoupleLoaded({ data1, data2, coupleId });
            }
          } else {
            // Single patient / Oocyte Freezing fallback
            setIsCouple(false);
            const single = list[0] || patientsData;
            const name = `${single.titles || ''} ${single.first_name || ''} ${single.last_name || ''}`.trim();
            setFormData(prev => ({
              ...prev,
              coupleId: '',
              isSingleFemale: true,
              patientId2: single.p_uhid,
              patientName2: name,
              gender2: single.gender || 'Female',
              age2: single.age || '',
              patientId: single.p_uhid,
              patientName: name
            }));
          }
        }
      } catch (err) {
        console.error('Error fetching couple details in CouplePatientInfo:', err);
      } finally {
        loaderchange?.('false');
      }
    };

    fetchData();
  }, [patientsData?.p_uhid, patientsData?.couple_id, patientsData?.purpose]);

  // Handle interactive UHID search in edit mode
  const handleUhidSearch = e => {
    const { name, value } = e.target;
    if (handleChange) handleChange(e);

    if (/^(ASCAS|VDASCAS)\d+$/i.test((value || '').trim())) {
      const fetchPartner = async () => {
        loaderchange?.('true');
        try {
          const action = fetchPatientDetailsUuid({ p_uhid: value.trim() });
          const response = await dispatch(action);
          if (response.payload?.status?.code === 200) {
            const list = response.payload.data?.data || [];
            if (list.length >= 2) {
              setIsCouple(true);
              const data1 =
                list[0]?.gender === 'Male' ? list[0] : list[1] || list[0];
              const data2 =
                list[0]?.gender === 'Female' ? list[0] : list[1] || list[1];

              const namePat1 = `${data1?.titles || ''} ${data1?.first_name || ''} ${data1?.last_name || ''}`.trim();
              const namePat2 = `${data2?.titles || ''} ${data2?.first_name || ''} ${data2?.last_name || ''}`.trim();
              const cId = data1?.couple_id || data2?.couple_id || '';

              setFormData(prev => ({
                ...prev,
                coupleId: cId,
                isSingleFemale: false,
                patientId1: data1?.p_uhid || '',
                patientName1: namePat1,
                gender1: data1?.gender || 'Male',
                age1: data1?.age || '',
                patientId2: data2?.p_uhid || '',
                patientName2: namePat2,
                gender2: data2?.gender || 'Female',
                age2: data2?.age || '',
                patientOne_UUId: data1?.id || '',
                patientTwo_UUId: data2?.id || ''
              }));
            }
          }
        } catch (err) {
          console.error(err);
        } finally {
          loaderchange?.('false');
        }
      };
      fetchPartner();
    }
  };

  const coupleIdValue = formData.coupleId || patientsData?.couple_id || '';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
        gap: '8px',
        backgroundColor: '#f7f7f7',
        borderRadius: '6px',
        padding: '16px 12px',
        width: '100%',
        margin: '10px 0'
      }}>


      {/* Oocyte Freezing / Single Female Notice */}
      {!isCouple && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            padding: '6px 12px',
            backgroundColor: '#fce8e6',
            borderRadius: '5px',
            border: '1px solid #fad2cf',
            marginBottom: '4px'
          }}>
          <span style={{ fontWeight: 600, fontSize: '13px', color: '#c5221f' }}>
            Individual Patient Record (Oocyte Freezing)
          </span>
          <span style={{ fontSize: '12px', color: '#5f6368' }}>
            UHID: {formData.patientId2 || patientsData.p_uhid}
          </span>
        </div>
      )}

      {isCouple ? (
        /* Regular Infertility / IVF Couple Layout (Male & Female Side-by-Side) */
        <div className='row w-100' style={{ margin: 0 }}>
          {/* Partner 1 (Male) */}
          <div
            className='col-md-6'
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              borderRight: '1px solid #e0e0e0',
              paddingRight: '16px'
            }}>
            <div
              style={{
                fontSize: '12px',
                fontWeight: 700,
                color: '#1976d2',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                borderBottom: '1px dashed #bbdefb',
                paddingBottom: '4px',
                marginBottom: '4px'
              }}>
              Husband / Male Partner
            </div>
            <FormInputWithLabel
              label='Patient ID'
              name='patientId1'
              value={formData.patientId1 || ''}
              required={false}
              onChange={handleUhidSearch}
              inputClassName='show'
              minWidth='140px'
            />
            <FormInputWithLabel
              label='Patient Name'
              name='patientName1'
              value={formData.patientName1 || ''}
              required={false}
              onChange={handleChange}
              inputClassName='show'
              minWidth='140px'
            />
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '145px 16px 300px',
                alignItems: 'center'
              }}>
              <span style={{ fontWeight: 600 }}>Gender</span>
              <span style={{ fontWeight: 600 }}>:</span>
              <select
                className={`createTime ${show ? 'hide' : 'show'}`}
                onChange={handleChange}
                value={formData.gender1 || 'Male'}
                name='gender1'
                style={{
                  border: '1px solid #ddd',
                  outline: 'none',
                  padding: '10px',
                  backgroundColor: 'white'
                }}>
                <option value='Male'>Male</option>
                <option value='Female'>Female</option>
                <option value='Others'>Others</option>
                <option value='Decline to State'>Decline to State</option>
              </select>
              <span className={`input ${!show ? 'hide' : 'show'}`}>
                &nbsp;{formData.gender1 || 'Male'}
              </span>
            </div>
            <FormInputWithLabel
              label='Age'
              name='age1'
              value={formData.age1 || ''}
              required={false}
              onChange={handleChange}
              inputClassName='show'
              minWidth='140px'
            />
          </div>

          {/* Partner 2 (Female) */}
          <div
            className='col-md-6'
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              paddingLeft: '16px'
            }}>
            <div
              style={{
                fontSize: '12px',
                fontWeight: 700,
                color: '#c2185b',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                borderBottom: '1px dashed #f8bbd0',
                paddingBottom: '4px',
                marginBottom: '4px'
              }}>
              Wife / Female Partner
            </div>
            <FormInputWithLabel
              label='Patient ID'
              name='patientId2'
              value={formData.patientId2 || ''}
              required={false}
              onChange={handleUhidSearch}
              inputClassName='show'
              minWidth='140px'
            />
            <FormInputWithLabel
              label='Patient Name'
              name='patientName2'
              value={formData.patientName2 || ''}
              required={false}
              onChange={handleChange}
              inputClassName='show'
              minWidth='140px'
            />
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '145px 16px 300px',
                alignItems: 'center'
              }}>
              <span style={{ fontWeight: 600 }}>Gender</span>
              <span style={{ fontWeight: 600 }}>:</span>
              <select
                className={`createTime ${show ? 'hide' : 'show'}`}
                onChange={handleChange}
                value={formData.gender2 || 'Female'}
                name='gender2'
                style={{
                  border: '1px solid #ddd',
                  outline: 'none',
                  padding: '10px',
                  backgroundColor: 'white'
                }}>
                <option value='Female'>Female</option>
                <option value='Male'>Male</option>
                <option value='Others'>Others</option>
                <option value='Decline to State'>Decline to State</option>
              </select>
              <span className={`input ${!show ? 'hide' : 'show'}`}>
                &nbsp;{formData.gender2 || 'Female'}
              </span>
            </div>
            <FormInputWithLabel
              label='Age'
              name='age2'
              value={formData.age2 || ''}
              required={false}
              onChange={handleChange}
              inputClassName='show'
              minWidth='140px'
            />
          </div>
        </div>
      ) : (
        /* Oocyte Freezing / Single Female Patient Layout */
        <div className='row w-100' style={{ margin: 0 }}>
          <div
            className='col-md-8'
            style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <FormInputWithLabel
              label='Patient ID'
              name='patientId2'
              value={formData.patientId2 || patientsData.p_uhid || ''}
              required={false}
              onChange={handleChange}
              inputClassName='show'
              minWidth='140px'
            />
            <FormInputWithLabel
              label='Patient Name'
              name='patientName2'
              value={
                formData.patientName2 ||
                `${patientsData.titles || ''} ${patientsData.first_name || ''} ${patientsData.last_name || ''}`.trim()
              }
              required={false}
              onChange={handleChange}
              inputClassName='show'
              minWidth='140px'
            />
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '145px 16px 300px',
                alignItems: 'center'
              }}>
              <span style={{ fontWeight: 600 }}>Gender</span>
              <span style={{ fontWeight: 600 }}>:</span>
              <select
                className={`createTime ${show ? 'hide' : 'show'}`}
                onChange={handleChange}
                value={formData.gender2 || patientsData.gender || 'Female'}
                name='gender2'
                style={{
                  border: '1px solid #ddd',
                  outline: 'none',
                  padding: '10px',
                  backgroundColor: 'white'
                }}>
                <option value='Female'>Female</option>
                <option value='Male'>Male</option>
                <option value='Others'>Others</option>
                <option value='Decline to State'>Decline to State</option>
              </select>
              <span className={`input ${!show ? 'hide' : 'show'}`}>
                &nbsp;{formData.gender2 || patientsData.gender || 'Female'}
              </span>
            </div>
            <FormInputWithLabel
              label='Age'
              name='age2'
              value={formData.age2 || patientsData.age || ''}
              required={false}
              onChange={handleChange}
              inputClassName='show'
              minWidth='140px'
            />
          </div>
        </div>
      )}
    </div>
  );
}
